import { LOBState } from "stores/wizard/state";
import { ThirdPartyPackageConfig } from "stores/wizard/config";
import { ThirdPPLastSearchData, LastSearchData } from "../../LastSearchData";
import { safeResponseToJson } from "components/utility/FetchUtils";
import { observable, action, computed } from "mobx";
import { GlobalWizardState, TravelersState } from "../global";
import { Location3PPDestination, ThirdPPDuration } from "./typings";
import { travelersMetadata } from "src/components/shared/TravelersField/utils";
import { Room, Travelers } from "src/components/shared/TravelersField/typings";
import { setCookie } from "components/utility/cookie";

export class ThirdPartyPackageWizardState implements LOBState {
  private globalState: GlobalWizardState;

  @computed
  public get location() {
    return this.globalState.location;
  }

  @observable
  public subLOBState = "";

  //Since this LOB doesn't have a subLOB, setting this as empty so the queryParam is removed when navigating to this LOB
  @action
  public updateSubLOBState(subLOBState: string) {
    return false;
  }

  @computed
  public get travelersValueChanged() {
    return this.globalState.travelersValueChanged;
  }

  @action
  public setTravelersValue = () => {
    this.globalState.setTravelersValue();
  };

  //We have this method to achieve the LOBState implementation
  @action
  public updateDestinationSelection = (selection: any) => {
    this.globalState.updateDestinationSelection(selection);
    this.destinationInvalidKey = "";
  };

  public config: ThirdPartyPackageConfig;

  public lastSearchData?: ThirdPPLastSearchData;

  public siteid: number;

  public locale: string;

  @computed
  public get canTrack() {
    return this.globalState.canTrack;
  }

  @observable
  public offersWithTransfer = false;

  @observable
  public destination = "";

  @observable
  public origin = "";

  @observable
  public duration = "";

  @observable
  public destinations: any[] = [];

  @observable
  public origins: any[] = [];

  @observable
  public durations: any[] = [];

  @observable
  public dateStartInvalidKey = "";

  @observable
  public dateEndInvalidKey = "";

  @observable
  public destinationInvalidKey = "";

  @observable
  public originInvalidKey = "";

  @observable
  public durationInvalidKey = "";

  @observable
  public nonHotelTravelersInvalidKey = "";

  @observable
  public formAction = "";

  @observable
  public errorInputRef: React.RefObject<HTMLHeadingElement> | null;

  @observable
  public numberOfErrors = 0;

  public wizardInputsArray: React.RefObject<HTMLInputElement>[] = [];

  @computed
  public get date() {
    return this.globalState.date;
  }

  @computed
  public get useLastSearchForMultiLOB() {
    return this.globalState.config.useLastSearchForMultiLOB;
  }

  public get useLastSearch() {
    return this.useLastSearchForMultiLOB || this.config.lastSearch.useLastSearchForSingleLOB;
  }

  constructor(config: ThirdPartyPackageConfig, globalState: GlobalWizardState, lastSearchData?: LastSearchData) {
    this.config = config;
    this.origins = config.origins || [];
    this.destinations = config.destinations || [];
    this.durations = config.durations || [];
    this.globalState = globalState;
    if (lastSearchData && this.canTrack) {
      this.lastSearchData = lastSearchData.thirdPP;
    }
  }

  private selectFirstOrigin = () => {
    if (this.origins.length) {
      this.setOrigin(this.origins[0].code);
    }
  };

  private selectExistOrFirstDestination = () => {
    if (this.destinations.length) {
      const newDestination =
        this.destination && this.destinations.find((destination) => destination.code.toString() === this.destination)
          ? this.destination
          : this.destinations[0].code;
      this.setDestination(newDestination);
    }
  };

  private selectExistOrFirstDuration = () => {
    if (this.durations.length) {
      const newDuration =
        this.duration && this.durations.find((duration) => duration.value.toString() === this.duration)
          ? this.duration
          : this.durations[0].value;
      this.setDuration(newDuration);
    }
  };

  public async loadOrigins(siteid: number): Promise<any> {
    this.siteid = siteid;
    await this.updateOrigins();
  }

  @action
  public toggleOffersWithTransfer() {
    this.offersWithTransfer = !this.offersWithTransfer;
  }

  @action
  public setOrigin(value: string, selectFirstDestination = true) {
    this.origin = value;
    this.updateDestinations();
    this.updateFormAction();
    if (this.globalState.isFormSubmitted) {
      this.validateOriginField();
    }
    // If siteid requires api call this function will be called once the api request is done
    if (selectFirstDestination && !this.config.siteIdsWithApiCall.includes(this.siteid)) {
      this.selectExistOrFirstDestination();
    }
  }

  @action
  public setDestination(value: string) {
    this.destination = value;
    this.updateDurations();
    this.updateFormAction();
    if (this.globalState.isFormSubmitted) {
      this.validateDestinationField();
    }
    this.selectExistOrFirstDuration();
  }

  @action
  public setDuration(value: string) {
    this.duration = value;
    if (this.globalState.isFormSubmitted) {
      this.validateDurationField();
    }
  }

  @action
  public updateDurations() {
    if (this.config.siteIdsWithApiCall.includes(this.siteid)) {
      this.getDurationsFromApi();
    }
  }

  @action
  public updateDestinations() {
    if (this.config.siteIdsWithApiCall.includes(this.siteid)) {
      this.getDestinationsFromApi();
    }
  }

  public async updateOrigins(): Promise<any> {
    if (this.config.siteIdsWithApiCall.includes(this.siteid)) {
      this.origin = "";

      await this.getOriginsFromApi();
    } else {
      this.selectFirstOrigin();
    }

    return true;
  }

  @action
  public updateFormAction() {
    const url = this.generateFormAction();
    this.formAction = url;
  }

  @action
  public validateMaxDateRange = () => {
    // Check if it is not a single date
    if (this.config.date.queryParam.end) {
      if (!this.globalState.validateMaxDateRange(this.date.start, this.date.end, this.config.date.maxDaysRange)) {
        this.dateEndInvalidKey = this.config.date.invalidMaxDatesRangeMessageToken;
        if (!this.errorInputRef) {
          this.errorInputRef = this.findFocusElement("d2")!;
        }

        return false;
      }
    }

    return true;
  };

  @action
  public validateEndDateField = () => this.validateMaxDateRange();

  @action
  public resetValidations = () => {
    this.destinationInvalidKey = "";
    this.durationInvalidKey = "";
    this.originInvalidKey = "";
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";

    return true;
  };

  @action findFocusElement = (keyword: string) =>
    this.wizardInputsArray.find((element) => element.current !== null && element.current.outerHTML.includes(keyword));

  @action
  public validateOriginField = (): boolean => {
    if (this.origin && this.origin.toString().trim() !== "") {
      this.originInvalidKey = "";

      return true;
    }
    this.originInvalidKey = this.config.origin.invalidMessage;
    this.errorInputRef = this.findFocusElement("origin")!;

    return false;
  };

  @action
  public validateDurationField = (): boolean => {
    if (this.duration && this.duration.toString().trim() !== "") {
      this.durationInvalidKey = "";

      return true;
    }

    this.durationInvalidKey = this.config.duration.invalidMessage;
    if (!this.errorInputRef) {
      this.errorInputRef = this.findFocusElement("duration")!;
    }

    return false;
  };

  @action
  public validateDestinationField = (): boolean => {
    if (this.destination && this.destination.toString().trim() !== "") {
      this.destinationInvalidKey = "";

      return true;
    }
    this.destinationInvalidKey = this.config.destination.invalidMessage;
    if (!this.errorInputRef) {
      this.errorInputRef = this.findFocusElement("destination")!;
    }

    return false;
  };

  @action
  public validateChildInfantFields = () => {
    if (!this.config.travelers.withRooms && !this.globalState.validateChildInfantFields(this.travelers)) {
      this.nonHotelTravelersInvalidKey = this.config.travelers.invalidChildInfantValuesMessageToken!;
      return false;
    }
    return true;
  };

  @action
  public validateTravelersField = () => this.validateLessThanNTravelers() && this.validateChildInfantFields();

  @action
  public validateLessThanNTravelers = () => {
    if (!this.globalState.validateLessThanNTravelers(this.nonHotelTravelersMetadata, this.config.travelers)) {
      this.nonHotelTravelersInvalidKey = this.config.travelers.invalidLessThanNTravelersMessageToken;

      return false;
    }

    return true;
  };

  public validateForm = () => {
    if (this.errorInputRef) {
      this.errorInputRef = null;
    }
    this.numberOfErrors = 0;

    const validations: boolean[] = [
      this.validateOriginField(),
      this.validateDestinationField(),
      this.validateDurationField(),
      this.validateEndDateField(),
      this.validateTravelersField(),
    ];

    validations.forEach((value) => !value && (this.numberOfErrors += 1));

    return !validations.includes(false);
  };

  // other actions
  @action
  public overrideConfig(callback: () => void): void {
    callback();
  }

  @action
  public updateDateSelection = (start: Date, end: Date) => {
    this.globalState.updateDateSelection(start, end);
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
  };

  @action
  public updateNonHotelTravelersSelection = (travelers: Travelers) => {
    this.globalState.updateNonHotelTravelersSelection(travelers);
    this.nonHotelTravelersInvalidKey = "";
  };

  @action moreThanOneError = () => {
    return this.globalState.moreThanOneError(this.numberOfErrors);
  };

  public setLastSearchCookie = () => {
    if (this.canTrack && this.useLastSearch) {
      const date = this.date;

      const lastSearchCookieObject: LastSearchData = {
        lob: this.config.lastSearch.lobKey,
        thirdPP: {
          origin: this.origin,
          destination: this.destination,
          start_date: date.start ? date.start.toString() : "",
          end_date: date.end ? date.end.toString() : "",
          travelers: JSON.stringify(this.travelers),
          duration: this.duration,
          offerWithTransfer: this.offersWithTransfer.toString(),
        },
      };
      setCookie({
        key: this.config.lastSearch.lastSearchKey,
        value: JSON.stringify(lastSearchCookieObject),
        days: 30,
      });
    }
  };

  public updateStateFromCookie = () => {
    const lastSearchData = this.lastSearchData;

    if (this.canTrack && this.useLastSearch && lastSearchData) {
      const startDateFromCookie = new Date(lastSearchData.start_date);
      const endDateFromCookie = lastSearchData.end_date ? new Date(lastSearchData.end_date) : new Date();

      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      const startDateToUse = startDateFromCookie >= today ? startDateFromCookie : today;
      const endDateToUse = endDateFromCookie >= tomorrow ? endDateFromCookie : tomorrow;

      const travelers: TravelersState = lastSearchData.travelers
        ? JSON.parse(lastSearchData.travelers)
        : this.travelers;

      this.destination = lastSearchData.destination || "";
      this.duration = lastSearchData.duration || "";
      this.setOrigin(lastSearchData.origin);

      this.offersWithTransfer = lastSearchData.offerWithTransfer
        ? JSON.parse(lastSearchData.offerWithTransfer)
        : this.offersWithTransfer;

      const nonHotelTravelers = travelers.nonHotel;
      this.updateDateSelection(startDateToUse, endDateToUse);
      this.updateNonHotelTravelersSelection(nonHotelTravelers);
    }
  };

  @computed
  public get travelers() {
    const nonHotelRoom: [Room] = [
      {
        adults: 2,
        children: [],
        infants: [],
      },
    ];

    const applicableTravelerState = this.globalState.travelers.nonHotel;
    // Loop through existing room Info for travelers to find applicable room
    const applicableRoomsForTravelers: Room[] = applicableTravelerState.rooms.map((room: Room) => {
      const applicableAdultsPerRoom =
        room.adults > this.config.travelers.maxAdultsPerRoom ? this.config.travelers.maxAdultsPerRoom : room.adults;
      const applicableChildrenPerRoom =
        room.children.length > this.config.travelers.maxChildrenPerRoom
          ? room.children.splice(0, this.config.travelers.maxChildrenPerRoom)
          : room.children;
      const applicableInfantsPerRoom = this.config.travelers.withInfants
        ? room.infants.length > this.config.travelers.maxInfantsPerRoom
          ? room.infants.splice(0, this.config.travelers.maxInfantsPerRoom)
          : room.infants
        : [];

      return {
        adults: applicableAdultsPerRoom,
        children: applicableChildrenPerRoom,
        infants: applicableInfantsPerRoom,
      };
    });

    // Only the first room details to be preserved in nonHotel [Room]
    nonHotelRoom[0] = applicableRoomsForTravelers[0];

    const thirdTravelers = {
      hotel: {
        rooms: applicableRoomsForTravelers,
        infantSeating: this.globalState.travelers.hotel.infantSeating,
      },
      hotelPackage: {
        rooms: applicableRoomsForTravelers,
        infantSeating: this.globalState.travelers.hotelPackage.infantSeating,
      },
      nonHotel: {
        rooms: nonHotelRoom,
        infantSeating: this.globalState.travelers.nonHotel.infantSeating,
      },
      cruise: {
        rooms: nonHotelRoom,
        infantSeating: this.globalState.travelers.cruise.infantSeating,
      },
    };

    return thirdTravelers;
  }

  @computed
  public get nonHotelTravelersMetadata() {
    return travelersMetadata(this.travelers.nonHotel);
  }

  public submit = (event: React.FormEvent) => {
    const isValidForm = this.validateForm();
    if (!isValidForm) {
      event.preventDefault();
    }

    if (this.canTrack && this.useLastSearch) {
      this.setLastSearchCookie();
    }
    this.globalState.submit();
  };

  private findDestination = (destinationCode: string): Location3PPDestination | undefined => {
    return this.destinations.find((destination: Location3PPDestination) => destination.code === destinationCode);
  };

  private generateHeadersApi = () => ({
    Accept: "application/json",
    "Content-Type": "application/json",
  });

  private getDestinationsFromApi() {
    const updateDestinationFetch = () =>
      fetch(`/all-inclusive-vacation-api/v1/origins/${this.origin}/destinations`, {
        method: "GET",
        headers: {
          ...this.generateHeadersApi(),
        },
        credentials: "include",
      });

    return Promise.resolve(updateDestinationFetch())
      .then(safeResponseToJson)
      .then((data) => {
        const subDestinationsFormat = (destinations: Location3PPDestination[]) => {
          return destinations.map((elem) => {
            elem.name = ` - ${elem.name}`;

            return elem;
          });
        };

        const flattenDestinations = (arr: Location3PPDestination[]): any => {
          return arr.reduce((flat, toFlatten: any) => {
            return flat.concat(
              toFlatten.destinations && Array.isArray(toFlatten.destinations) && toFlatten.destinations.length
                ? [].concat(toFlatten).concat(flattenDestinations(subDestinationsFormat(toFlatten.destinations)))
                : toFlatten
            );
          }, []);
        };

        this.destinations = flattenDestinations(data);
        this.selectExistOrFirstDestination();
      });
  }

  private getOriginsFromApi() {
    const updateOriginFetch = () =>
      fetch(`/all-inclusive-vacation-api/v1/origins`, {
        method: "GET",
        headers: {
          ...this.generateHeadersApi(),
        },
        credentials: "include",
      });

    return Promise.resolve(updateOriginFetch())
      .then(safeResponseToJson)
      .then((data) => {
        this.origins = data;
      });
  }

  private getDurationsFromApi() {
    const destination: Location3PPDestination | undefined = this.findDestination(this.destination);
    if (destination && destination.durations) {
      this.durations = destination.durations.map(
        (duration): ThirdPPDuration => {
          const value: string = duration.from === duration.to ? duration.from : `${duration.from}-${duration.to}`;
          const { daysTextApi } = this.config.duration;
          const text: string =
            duration.from === duration.to
              ? `${duration.from} ${daysTextApi}`
              : `${duration.from} - ${duration.to} ${daysTextApi}`;

          return {
            value,
            text,
          };
        }
      );
    } else {
      this.durations = [];
    }
  }

  private generateFormAction() {
    if (this.config.siteIdsWithApiCall.includes(this.siteid)) {
      return `${this.config.form.action}/search`;
    }

    const splittedDestinationValue = this.destination.split(":");
    const url = splittedDestinationValue[1] ? "hotel" : "region";

    return `${this.config.form.action}/${url}`;
  }

  public updateDateFromConfig = () => {
    this.globalState.updateDateFromConfig(this.config.date);
  };
}
